import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Play, MapTrifold, Mountains, Path, Info, CaretRight, Compass as CompassIcon } from '@phosphor-icons/react';
import GpsPill from '../components/GpsPill';
import OfflineBadge from '../components/OfflineBadge';
import { readOnce } from '../lib/gps';
import { getSetting, setSetting, listTreks, saveTrek } from '../lib/db';
import { buildDemoTrek } from '../lib/simulator';
import { fmtDistance } from '../lib/geo';

const HERO_BG = 'https://images.unsplash.com/photo-1460355976672-71c3f0a4bdac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxuaWdodCUyMGFscGluZSUyMG1vdW50YWluJTIwZGFya3xlbnwwfHx8fDE3ODc3NjEzNDF8MA&ixlib=rb-4.1.0&q=85';

export default function Home() {
  const nav = useNavigate();
  const [accuracy, setAccuracy] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [recentCount, setRecentCount] = useState(0);
  const [demoLoaded, setDemoLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const pos = await readOnce();
        setAccuracy(pos.accuracy);
        setGpsError(null);
      } catch (err) {
        setGpsError(err.message || 'Location permission required');
      }
    })();
    (async () => {
      const treks = await listTreks();
      setRecentCount(treks.length);
      setDemoLoaded(treks.some((t) => t.isDemo));
    })();
  }, []);

  const handleStart = async () => {
    const sim = await getSetting('simulator', false);
    if (!sim) {
      // Ensure permission is granted before entering active screen.
      try {
        const pos = await readOnce();
        setAccuracy(pos.accuracy);
      } catch (err) {
        toast.error('Location permission required', {
          description: 'Grant location access, or enable Simulator in Settings to test.',
        });
        return;
      }
    }
    nav('/trek');
  };

  const loadDemo = async () => {
    const t = buildDemoTrek();
    await saveTrek(t);
    setDemoLoaded(true);
    setRecentCount((c) => c + 1);
    toast.success('Demo trek loaded', { description: 'Check "My Treks" and try Return to Start.' });
  };

  return (
    <div className="min-h-[100dvh] pb-28 relative">
      {/* Hero background */}
      <div
        className="absolute inset-x-0 top-0 h-[420px] -z-10 opacity-70"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(10,10,10,0.35) 0%, rgba(10,10,10,0.85) 60%, #0A0A0A 100%), url(${HERO_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="absolute inset-x-0 top-0 h-[420px] -z-10 topo-pattern opacity-40" />

      <header className="safe-top px-5 pt-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#FF9F1C] flex items-center justify-center">
            <Mountains size={18} weight="fill" color="#0A0A0A" />
          </div>
          <span className="font-display font-black tracking-tight text-lg" data-testid="app-title">TrailBack</span>
        </div>
        <OfflineBadge />
      </header>

      <section className="px-5 mt-10">
        <div className="text-[11px] uppercase tracking-[0.28em] text-[#FFD166] font-bold">Offline Trek Navigation</div>
        <h1 className="mt-2 font-display font-black text-4xl sm:text-5xl leading-[1.05]" data-testid="hero-title">
          Remember your trail.<br />
          <span className="text-[#FF9F1C]">Find your way back.</span>
        </h1>
        <p className="mt-3 text-sm text-zinc-300 max-w-[36ch]">
          Records every step with GPS. Guides you home along the exact path you took — no signal needed.
        </p>

        <div className="mt-6">
          <GpsPill accuracy={accuracy} />
        </div>
      </section>

      <section className="px-5 mt-8">
        <button
          onClick={handleStart}
          data-testid="start-trek-button"
          className="btn-tap w-full h-20 rounded-2xl bg-[#FF9F1C] text-black font-display font-black text-2xl tracking-tight shadow-[0_10px_40px_-10px_rgba(255,159,28,0.75)] flex items-center justify-center gap-3 active:bg-[#FFB347]"
        >
          <Play size={30} weight="fill" />
          START TREK
        </button>
        {gpsError && (
          <div data-testid="gps-error" className="mt-3 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {gpsError}. You can still test using the GPS Simulator in Settings.
          </div>
        )}
      </section>

      <section className="px-5 mt-8 grid grid-cols-2 gap-3">
        <button
          onClick={() => nav('/treks')}
          data-testid="view-my-treks"
          className="btn-tap glass-panel rounded-2xl p-4 text-left active:bg-white/5"
        >
          <Path size={22} weight="bold" color="#FF9F1C" />
          <div className="mt-2 font-display font-bold">My Treks</div>
          <div className="text-xs text-zinc-400 mt-0.5">{recentCount} saved</div>
        </button>
        <button
          onClick={() => nav('/settings')}
          data-testid="quick-settings"
          className="btn-tap glass-panel rounded-2xl p-4 text-left active:bg-white/5"
        >
          <CompassIcon size={22} weight="bold" color="#06D6A0" />
          <div className="mt-2 font-display font-bold">Settings</div>
          <div className="text-xs text-zinc-400 mt-0.5">Simulator, thresholds</div>
        </button>
      </section>

      <section className="px-5 mt-6">
        {!demoLoaded ? (
          <button
            onClick={loadDemo}
            data-testid="load-demo-button"
            className="btn-tap w-full glass-panel rounded-2xl px-4 py-3 flex items-center justify-between active:bg-white/5"
          >
            <div className="flex items-center gap-3">
              <MapTrifold size={20} weight="bold" color="#06D6A0" />
              <div className="text-left">
                <div className="font-display font-bold text-sm">Load Sample Trek</div>
                <div className="text-[11px] text-zinc-400">Try Return-to-Start with demo data</div>
              </div>
            </div>
            <CaretRight size={18} />
          </button>
        ) : (
          <div className="glass-panel rounded-2xl px-4 py-3 text-xs text-zinc-400 flex items-center gap-2">
            <Info size={16} />
            Demo trek loaded — open it from My Treks.
          </div>
        )}
      </section>

      <section className="px-5 mt-6 text-[11px] text-zinc-500 leading-relaxed">
        <p>
          <span className="text-zinc-300 font-semibold">Privacy:</span> All routes are stored on this device only. TrailBack never uploads your location.
        </p>
      </section>
    </div>
  );
}

export function fmt(n) { return fmtDistance(n); }
